import { useEffect, useState } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';

const TOGGLES = [
    { key: 'enabled', label: 'Focus Guard', hint: 'Blur the page whenever a learner switches tab, window or app. Admins and Quality admins are never affected.' },
    { key: 'mouse_leave', label: 'Blur when the mouse leaves the page', hint: 'Also counts the mouse leaving the page for more than a second.' },
    { key: 'paste_block', label: 'Block pasting into answers', hint: 'Learners cannot paste into answer boxes, chats or the code editor. Password fields still accept paste.' },
    { key: 'fullscreen', label: 'Full screen during simulations and quizzes', hint: 'Simulations and quizzes stay hidden until the learner enters full screen.' },
];

const ToggleRow = ({ item, checked, disabled, onChange }) => (
    <div className="flex items-start justify-between gap-4 rounded-xl border p-4">
        <div className="space-y-1">
            <Label htmlFor={`fg-${item.key}`} className="text-sm font-semibold">{item.label}</Label>
            <p className="text-xs text-muted-foreground">{item.hint}</p>
        </div>
        <Switch id={`fg-${item.key}`} checked={checked} disabled={disabled} onCheckedChange={onChange} data-testid={`focus-guard-toggle-${item.key}`} />
    </div>
);

// Admin on/off switches for the learner anti-malpractice protections
export const FocusGuardSettings = () => {
    const { api } = useAuth();
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get('/focus-guard/config').then(({ data }) => setForm(data.settings)).catch(() => toast.error('Could not load Focus Guard settings'));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const save = async () => {
        setSaving(true);
        try {
            const strikeLimit = Math.min(10, Math.max(1, Math.round(Number(form.strike_limit)) || 3));
            const { data } = await api.put('/focus-guard/settings', { ...form, strike_limit: strikeLimit });
            setForm(data);
            toast.success('Focus Guard settings saved');
        } catch (err) {
            toast.error(err.response?.data?.detail?.[0]?.msg || 'Could not save Focus Guard settings');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card data-testid="focus-guard-settings">
            <CardHeader>
                <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> Focus Guard (learner security)</CardTitle>
                <CardDescription>Stops learners leaving the page to look up answers in tools like ChatGPT or Claude. Applies on every page.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
                {!form ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : (
                    <>
                        {TOGGLES.map((item) => (
                            <ToggleRow key={item.key} item={item} checked={form[item.key]} disabled={item.key !== 'enabled' && !form.enabled}
                                onChange={(v) => setForm({ ...form, [item.key]: v })} />
                        ))}
                        <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border p-4">
                            <div className="space-y-1">
                                <Label htmlFor="fg-strikes" className="text-sm font-semibold">Strikes before sign-out</Label>
                                <p className="text-xs text-muted-foreground">On this strike, any running simulation or quiz ends and the learner is signed out.</p>
                            </div>
                            <Input id="fg-strikes" type="number" min={1} max={10} className="w-24" value={form.strike_limit} disabled={!form.enabled}
                                onChange={(e) => setForm({ ...form, strike_limit: e.target.value })} data-testid="focus-guard-strike-limit-input" />
                        </div>
                        <Button onClick={save} disabled={saving} data-testid="focus-guard-save-btn">
                            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save Focus Guard settings
                        </Button>
                    </>
                )}
            </CardContent>
        </Card>
    );
};
